import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_auth/help')({
  component: HelpPage,
});

const SECTIONS = [
  {
    id: 'sessions',
    title: 'Sessions and your plan',
    body: [
      'A session is one voice conversation between our AI and a person: a candidate taking a screening, a participant in a research interview, or a learner going through a lesson.',
      'Your plan includes a number of sessions per billing period. One is used each time somebody starts a session. Creating, editing, sharing and testing sessions yourself is free and does not count.',
      'Billing shows how many sessions are left this period. When you run out, people who open your link see that the session is temporarily unavailable until you upgrade or the period resets.',
    ],
    link: { to: '/billing', label: 'See usage and plans' },
  },
  {
    id: 'create',
    title: 'Creating sessions with chat',
    body: [
      'Describe what you need in plain language on the home page: "Customer support screening, check English and empathy", "User interview about our onboarding flow", or "Teach me the basics of options trading".',
      'We pick the right format for you. Recruiters and researchers usually get a single session; a topic to learn becomes a group of sessions.',
      'After a session group is created you can keep chatting to change language, voice, or content. A single session opens as a finished card — use Open session to edit it, or Create another to start a new one.',
    ],
  },
  {
    id: 'results',
    title: 'Results and candidates',
    body: [
      'For screening and interview sessions, click the card on the home page to open the candidates inbox. Review each conversation, transcript and score, move people between stages, leave comments and export reports.',
      'Learning sessions and courses open for editing instead. Use the pencil icon to customize a session and the play icon to try it yourself. Screening groups also have a candidates icon on the card.',
    ],
  },
  {
    id: 'groups',
    title: 'Session groups and learning paths',
    body: [
      'A session group is a set of sessions that belong together: a multi-round screening, a research study with several interviews, or a learning path. Groups show as stacked cards.',
      'Learning paths additionally track progress and list what the learner will achieve. Use them for courses on any topic — history, languages, cooking, and more.',
    ],
  },
] as const;

function openSupportChat() {
  if (typeof window !== 'undefined' && window.$crisp) {
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
  }
}

function HelpPage() {
  return (
    <div className="mx-auto max-w-2xl space-y-10 py-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-900">Help</h1>
        <p className="text-sm text-neutral-500">How sessions, plans and results work.</p>
      </div>

      {SECTIONS.map((section) => (
        <section key={section.id} id={section.id} className="space-y-2.5">
          <h2 className="text-base font-semibold text-neutral-900">{section.title}</h2>
          {section.body.map((paragraph) => (
            <p key={paragraph} className="text-sm leading-6 text-neutral-600">
              {paragraph}
            </p>
          ))}
          {'link' in section && section.link && (
            <Link
              to={section.link.to}
              className="inline-flex rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
            >
              {section.link.label}
            </Link>
          )}
        </section>
      ))}

      <section className="rounded-2xl bg-neutral-50 p-5 ring-1 ring-black/5">
        <h2 className="text-sm font-semibold text-neutral-900">Still stuck?</h2>
        <p className="mt-1 text-sm text-neutral-500">Message us and a human will answer.</p>
        <Button type="button" variant="default" size="sm" className="mt-3" onClick={openSupportChat}>
          Chat with support
        </Button>
      </section>
    </div>
  );
}
